import { mdiAccountCancelOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { useCollection } from '@nandorojo/swr-firestore';
import {
  collection, getDocs, limit, onSnapshot, orderBy, query, startAfter,
} from 'firebase/firestore';
import Image from 'next/image';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { db } from '../../../firebase/firebase';
import { useAuth } from '../../../providers/AuthProvider';
import nomessages from '../../../public/assets/images/no-messages.png';
import Alert from '../../General/Alert';
import InboxListItem from './InboxListItem';

const ITEMS_PER_PAGE = 5;

const InboxList = () => {
  const router = useRouter();
  const { isAdmin, uid } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [changedItems, setChangedItems] = useState([]);
  const [toggleBlockedChats, setToggleBlockedChats] = useState(false);

  let theUid = uid;

  if (router.query.uid && isAdmin) {
    theUid = router.query.uid;
  }

  const {
    data: inboxListItems,
    mutate: inboxListItemsMutate,
    error: errorChats,
  } = useCollection(
    theUid ? `userChatrooms/${theUid}/chatrooms` : null,
    {
      orderBy: ['lastMessageDate', 'desc'],
      limit: ITEMS_PER_PAGE,
      ignoreFirestoreDocumentSnapshotField: false,
      listen: false,
    },
    {
      revalidateOnFocus: false,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
      refreshInterval: 0,
    },
  );

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'userChatrooms', theUid, 'chatrooms'), orderBy('lastMessageDate', 'desc'), limit(1)),
      (querySnapshot) => {
        const newItems = [];
        querySnapshot.docChanges().forEach((change) => {
          const obj = {
            ...change.doc.data(),
            id: change.doc.id,
            __snapshot: change.doc,
          };
          newItems.push(obj);
        });
        setChangedItems(newItems);
      },
    );

    return () => unsub();
  }, [theUid, setChangedItems]);

  useEffect(() => {
    if (typeof inboxListItems !== 'undefined' && changedItems.length) {
      const items = [...inboxListItems];
      changedItems.forEach((item) => {
        const ind = inboxListItems.findIndex((x) => x.id === item.id);
        if (ind !== -1) {
          items.splice(ind, 1);
        }
        items.unshift(item);
      });
      setChangedItems([]);
      inboxListItemsMutate(() => [...items], false);
    }
  }, [changedItems, setChangedItems, inboxListItems, inboxListItemsMutate]);

  const paginate = async (event) => {
    event.preventDefault();
    if (!inboxListItems || !inboxListItems.length) return;
    setIsLoading(true);

    const ref = collection(db, 'userChatrooms', theUid, 'chatrooms');

    // eslint-disable-next-line no-underscore-dangle
    const startAfterDocument = inboxListItems[inboxListItems.length - 1].__snapshot;

    const moreDocs = await getDocs(query(ref, orderBy('lastMessageDate', 'desc'), startAfter(startAfterDocument), limit(ITEMS_PER_PAGE)))
      .then((d) => {
        const docs = [];
        if (d.docs.length < ITEMS_PER_PAGE) {
          setShowMore(false);
        } else {
          setShowMore(true);
        }
        d.docs.forEach((doc) => docs.push({ ...doc.data(), id: doc.id, __snapshot: doc }));
        setIsLoading(false);
        return docs;
      })
      .catch(() => {
        setIsLoading(false);
        setShowMore(false);
      });

    inboxListItemsMutate((state) => [...state, ...moreDocs], false);
  };

  const { data: inboxListItemsBlocked, errorChatsBlocked } = useCollection(
    isAdmin && theUid ? `userChatrooms/${theUid}/blocked` : null,
    {
      orderBy: ['lastMessageDate', 'desc'],
      limit: 20,
      listen: true,
    },
  );

  const inboxList = inboxListItems?.map((inboxItem) => (
    <li key={inboxItem.id}>
      <InboxListItem chatroom={inboxItem} inbox={theUid} />
    </li>
  ));

  const inboxListBlocked = inboxListItemsBlocked?.map((inboxItem) => (
    <li key={inboxItem.id}>
      <InboxListItem chatroom={inboxItem} inbox={theUid} />
    </li>
  ));

  return (
    <>
      {isAdmin && (
        <ToggleButton
          toggle={toggleBlockedChats}
          setToggle={setToggleBlockedChats}
        />
      )}
      {toggleBlockedChats && isAdmin ? (
        inboxListItemsBlocked === undefined ? (
          <div className="loading-block" />
        ) : (
          inboxListItemsBlocked && inboxListItemsBlocked.length ? (
            <ShowChats
              chatList={inboxListBlocked}
              error={errorChatsBlocked}
            />
          ) : (
            <NoChats showBlocked />
          )
        )
      ) : (
        inboxListItems === undefined ? (
          <div className="loading-block" />
        ) : (
          inboxListItems && inboxListItems.length ? (
            <>
              <ShowChats chatList={inboxList} error={errorChats} />
              {inboxListItems.length === ITEMS_PER_PAGE || showMore ? (
                <button
                  type="button"
                  onClick={paginate}
                  className={`button is-fullwidth is-large${
                    isLoading ? ' is-loading' : ''
                  }`}
                >
                  Load more
                </button>
              ) : (
                ''
              )}
            </>
          ) : (
            <NoChats showBlocked={false} />
          )
        )
      )}
    </>
  );
};

const NoChats = ({ showBlocked }) => (
  <div className="hero">
    <div className="hero-body">
      <div className="content">
        {showBlocked ? (
          <>
            <h1 className="title">
              This user doesn&#39;t have any blocked chats
            </h1>
            <p>
              If this user blocks someone they have a direct chat with, that
              chat will be displayed here.
            </p>
          </>
        ) : (
          <>
            <h1 className="title">You don&#39;t have any messages yet</h1>
            <p>
              When you follow someone and they follow you, you will be able to
              send them a new message and chat with them here.
            </p>
            <Image src={nomessages} alt="" className="hero-decoration" />
          </>
        )}
      </div>
    </div>
  </div>
);

NoChats.propTypes = {
  showBlocked: PropTypes.bool.isRequired,
};

const ToggleButton = ({ toggle, setToggle }) => (
  <button
    type="button"
    className={`mt-4 is-dark button is-medium is-fullwidth ${
      toggle && 'is-danger'
    }`}
    onClick={() => setToggle(!toggle)}
  >
    {toggle ? (
      <>
        <span className="icon">
          <Icon path={mdiAccountCancelOutline} size={1.25} />
        </span>
        <span>Showing blocked chats</span>
      </>
    ) : (
      <span>Show blocked chats</span>
    )}
  </button>
);

ToggleButton.propTypes = {
  toggle: PropTypes.bool.isRequired,
  setToggle: PropTypes.func.isRequired,
};

const ShowChats = ({ chatList, error }) => (
  <>
    {error && <Alert type="error" message={error} />}
    <ul className="inbox-list">{chatList}</ul>
  </>
);

ShowChats.propTypes = {
  chatList: PropTypes.array.isRequired,
  error: PropTypes.any,
};

ShowChats.defaultProps = {
  error: undefined,
};

export default InboxList;
